import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";
import { ROUTE } from "@/lib/products";
import { ArrowRight, CheckCircle2, Bot, FileText, UserCheck, Sparkles } from "lucide-react";

const ICONS = {
  FIT_CHECK_FREE: Bot,
  REGRET_RISK_99: FileText,
  EXPERT_CALL_499: UserCheck,
  DECISION_PACK_999: Sparkles,
};

const PATH_BY_KEY = {
  FIT_CHECK_FREE: ROUTE.FIT_CHECK_FREE,
  REGRET_RISK_99: ROUTE.REGRET_RISK_99,
  EXPERT_CALL_499: ROUTE.EXPERT_CALL_499,
  DECISION_PACK_999: ROUTE.DECISION_PACK_999,
};

export default function ProductLadder({ compact = false }) {
  const [products, setProducts] = useState([]);
  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data.products || []));
  }, []);

  return (
    <div className={compact ? "" : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"} data-testid="product-ladder">
      <div className="text-center max-w-2xl mx-auto">
        <span className="inline-flex items-center gap-2 rounded-full bg-[#FDF9F1] px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[#C08E3B]">
          <Sparkles className="h-3.5 w-3.5" /> Free → Paid ladder
        </span>
        <h2 className="mt-4 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Pick the help you need — at each price point</h2>
        <p className="mt-3 text-slate-600">Every product is transparent about what's included, delivery time, and how the human expert is involved.</p>
      </div>
      <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {products.map((p) => {
          const Icon = ICONS[p.key] || Sparkles;
          const path = PATH_BY_KEY[p.key];
          const free = p.price === 0;
          return (
            <Link
              key={p.key}
              to={path}
              data-testid={`ladder-card-${p.key.toLowerCase()}`}
              onClick={() => track("product_view", { product_key: p.key, location: "ladder" })}
              className={`group relative flex flex-col rounded-2xl border p-6 transition-all ${
                free
                  ? "border-[#0B2A6B] bg-gradient-to-br from-white to-[#EEF2FA] shadow-md hover:-translate-y-1 hover:shadow-lg"
                  : "border-slate-200 bg-white hover:border-[#0B2A6B]/40 hover:-translate-y-1 hover:shadow-md"
              }`}
            >
              {free && <span className="absolute -top-3 left-6 rounded-full bg-[#D4A24C] px-3 py-1 text-[10px] font-bold uppercase text-white">Start here</span>}
              <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${free ? "bg-[#0B2A6B] text-white" : "bg-[#FDF9F1] text-[#C08E3B]"}`}>
                <Icon className="h-5 w-5" />
              </span>
              <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-slate-500">{free ? "Free entry" : `Priced at ₹${p.price}`}</p>
              <h3 className="mt-1 font-display text-lg font-bold text-slate-900">{p.name}</h3>
              <p className="mt-2 text-sm text-slate-600 flex-1">{p.deliverable}</p>
              <p className="mt-3 text-[11px] text-slate-400">{p.delivery}</p>
              <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-[#0B2A6B]">
                {p.cta} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
